import {RsnButton, RsnDialog} from '@raison/ui-kit';
import * as Sentry from '@sentry/react';
import {Atom, useAtomValue, useSetAtom} from 'jotai';
import {AtomWithQueryResult} from 'jotai-tanstack-query';
import {atomWithReducer} from 'jotai/utils';
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router';
import {UploadResult, useDocsUploadMutation} from '../api/useDocsUploadMutation';
import DisplayAttachments from './DisplayAttachments/DisplayAttachments';
import UploadButton from './UploadButton/UploadButton';
import {UploadProgressDialog} from './UploadProgressDialog';
import {DialogProps, UploadStatusDialog} from './UploadStatusDialog';

export type UploadRequirementProps = {
    docsStatus$: Atom<AtomWithQueryResult<DocsVerificationStatus>>;
};

export type DocsVerificationStatus = 'verified' | 'not_verified' | 'pending';

export function UploadDocs({
    uploadUrl,
    maxFilesAmount,
    docsStatus$,
    onSuccess,
}: UploadRequirementProps & {
    uploadUrl: string;
    maxFilesAmount: number;
    onSuccess?: () => void;
}) {
    const docsStatus = useAtomValue(docsStatus$);
    const uploadJob = useDocsUploadMutation(uploadUrl);

    const progress$ = useMemo(() => atomWithReducer(0, (_, next: number) => Math.floor(next)), []);
    const setProgress = useSetAtom(progress$);

    const [fileList, setFileList] = useState<File[]>([]);

    const handleSubmit = async (ev: React.FormEvent) => {
        ev.preventDefault();
        const result = await uploadJob.mutateAsync({files: fileList, setProgress}).catch(error => {
            setDialog({
                type: 'error',
                title: 'Something went wrong',
                message: 'Please try again later',
                onClose: uploadJob.reset,
            });
            throw error;
        });
        if (result.code === UploadResult.Abort) {
            return;
        }
        if (result.code === UploadResult.Success) {
            docsStatus.refetch();
            onSuccess?.();
        }
        if (result.code === UploadResult.Problem) {
            setDialog({
                type: 'error',
                title: 'Something went wrong',
                message: result.message,
                fileNames: result.fileNames,
                onClose: uploadJob.reset,
            });
        }
    };

    const state = useUploadStateDescription(fileList, docsStatus, uploadJob);

    const [dialog, setDialog] = useState<undefined | DialogProps>();
    const closeDialog = () => setDialog(undefined);

    return (
        <form className='flex h-full flex-col' onSubmit={handleSubmit}>
            <div>
                {fileList.length < maxFilesAmount && state !== 'files-uploaded' && (
                    <UploadButton
                        fileList={fileList}
                        setDialog={setDialog}
                        setFileList={setFileList}
                        disabled={state !== 'nothing-to-upload' && state !== 'can-upload'}
                        maxFilesAmount={maxFilesAmount}
                    />
                )}
                {fileList.length > 0 && (
                    <DisplayAttachments
                        hideRemove={state === 'files-uploaded'}
                        disableRemove={state === 'upload-in-progress'}
                        fileList={fileList}
                        setFileList={setFileList}
                    />
                )}
            </div>
            <div className='flex flex-1 flex-col justify-end'>
                {state == 'files-uploaded' ? (
                    <Fragment>
                        <div className='fv-text-m-regular my-1 text-center text-neutral-60'>
                            Files are being checked
                        </div>
                        <Link to='/assets'>
                            <RsnButton.Primary>Back to my accounts</RsnButton.Primary>
                        </Link>
                    </Fragment>
                ) : (
                    <RsnButton.Primary type='submit' disabled={state !== 'can-upload'}>
                        Send
                    </RsnButton.Primary>
                )}
            </div>
            <RsnDialog.Controller onClose={closeDialog}>
                {state === 'upload-in-progress' ? (
                    <UploadProgressDialog progress$={progress$} />
                ) : (
                    dialog && <UploadStatusDialog {...dialog} />
                )}
            </RsnDialog.Controller>
        </form>
    );
}

function useUploadStateDescription(
    fileList: File[],
    docsStatus: AtomWithQueryResult<DocsVerificationStatus>,
    uploadJob: ReturnType<typeof useDocsUploadMutation>
) {
    const state = (function describeState() {
        if (!docsStatus.data) return 'waiting-for-status';
        if (docsStatus.data !== 'not_verified') return 'files-uploaded';
        if (fileList.length === 0) return 'nothing-to-upload';

        switch (uploadJob.status) {
            case 'error':
            case 'idle':
                return 'can-upload';
            case 'pending':
                return 'upload-in-progress';
            case 'success': {
                if (uploadJob.data.code !== UploadResult.Success) return 'can-upload';
                if (docsStatus.dataUpdatedAt < uploadJob.data.successAt) return 'upload-in-progress';
                else return 'files-uploaded';
            }
        }
    })() satisfies string;

    useEffect(() => {
        Sentry.addBreadcrumb(composeBreadcrumb(state, docsStatus, uploadJob));
    }, [uploadJob.status, docsStatus.fetchStatus, uploadJob.status]);

    return state;
}

function composeBreadcrumb(
    state: string,
    docsStatus: AtomWithQueryResult<DocsVerificationStatus>,
    uploadJob: ReturnType<typeof useDocsUploadMutation>
): Sentry.Breadcrumb {
    return {
        level: 'debug',
        category: 'inspection',
        data: {
            computedState: state,
            docsStatus: {
                status: docsStatus.status,
                data: docsStatus.data,
                error: docsStatus.error,
                dataUpdatedAt: docsStatus.dataUpdatedAt,
                fetchStatus: docsStatus.fetchStatus,
            },
            uploadJob: {
                status: uploadJob.status,
                data: uploadJob.data,
                error: uploadJob.error,
                submittedAt: uploadJob.submittedAt,
                files: uploadJob.variables?.files.map(file => ({type: file.type, size: file.size})),
            },
        },
    };
}
